import * as React from 'react';
import { cn } from '../../lib/cn';

export type StatusPillStatus =
  | 'paid'
  | 'pending'
  | 'overdue'
  | 'draft'
  | 'posted'
  | 'sent'
  | 'submitted'
  | 'reconciled'
  | 'approved'
  | 'review'
  | 'rejected'
  | 'voided'
  | 'active'
  | 'inactive'
  | 'completed'
  | 'ready'
  | 'scheduled'
  | 'imported'
  | 'finalised'
  | 'filed'
  | 'superseded';

const STATUS_LABELS: Record<StatusPillStatus, string> = {
  paid: 'Paid',
  pending: 'Pending',
  overdue: 'Overdue',
  draft: 'Draft',
  // A manual journal / GL entry that has been committed to the ledger (left
  // the "draft" stage). Success-green, like `paid` — it is a settled,
  // committed state, not a pending or failure one. Distinct from `voided`
  // (red, reversed) and `draft` (neutral, not yet committed). Added for the
  // journal list (#25860). (`approved` was success-green too until #25157
  // moved it to teal, so it no longer matches this one either.)
  posted: 'Posted',
  sent: 'Sent',
  // An approval-workflow document that has left the author's hands and is
  // waiting on someone else. Distinct from `review` (a reviewer has picked it
  // up) and from `pending` (generic "not settled yet").
  submitted: 'Submitted',
  reconciled: 'Reconciled',
  approved: 'Approved',
  review: 'In Review',
  // Terminal negative outcome of an approval workflow. Distinct from
  // `overdue`, which is a deadline state, not a decision.
  rejected: 'Rejected',
  voided: 'Voided',
  active: 'Active',
  inactive: 'Inactive',
  completed: 'Completed',
  // Added for the VAT filing lifecycle panel (#22742): `approved` and `paid`
  // shared the exact same success-green dot+text color at the time
  // (`.status-approved`/`.status-paid` in glass.css), so reusing `approved`
  // for "ready to file" would have rendered visually IDENTICAL to the "paid"
  // state in the same 4-state panel — a real collision, not a hypothetical.
  // A new status member with its own color keeps all 4 filing states —
  // draft (gray) / ready (hollow navy ring) / filed=sent (blue) / paid
  // (green) — mutually distinct at a glance. See `.status-ready` in
  // glass.css. (#25157 independently moved `approved` to teal and gave
  // `ready` a hollow ring instead of a solid dot, so this status also now
  // reads distinctly from `reconciled`/`completed`, not just `paid`.)
  ready: 'Ready',
  // A future-dated change that is recorded but not in effect yet (e.g. a
  // salary change scheduled from a later date). Shares the calm accent dot of
  // `ready` -- it is neither a success nor a warning, just "not yet".
  scheduled: 'Scheduled',
  // A record brought in by the WagePro migration importer (a historical pay
  // run, or an employee created from the import) rather than entered natively.
  // The ONE canonical "imported" pill — never hand-roll a second badge for
  // this concept. Calm accent/navy family (like `reconciled`/`ready`): it is a
  // provenance marker, not a success or warning state.
  imported: 'Imported',
  // Tax-return lifecycle (Canada T2, #26522): `finalised` = frozen by the
  // preparer, not yet filed (calm accent, like `ready`); `filed` = recorded as
  // filed with the CRA (blue, like `sent`). The ONE canonical pair for a tax
  // return's state; `draft` is shared.
  finalised: 'Finalised',
  filed: 'Filed',
  // A tax-return version replaced by a reopen (Canada T2, #26659): kept as the
  // as-filed record, no longer the live return. Neutral (like `inactive`),
  // never danger: nothing went wrong, a later version simply took its place.
  superseded: 'Superseded',
};

interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  status: StatusPillStatus;
  /** Override the display label. Defaults to the built-in label for the status. */
  label?: string;
}

function StatusPill({ status, label, className, ...props }: StatusPillProps) {
  const displayLabel = label ?? STATUS_LABELS[status];
  return (
    <span
      className={cn('status-pill', `status-${status}`, className)}
      {...props}
    >
      {displayLabel}
    </span>
  );
}

export { StatusPill };
export type { StatusPillProps };
